import { useMemo } from 'react'
import { flatten } from 'shared/util'

import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToParentElement } from '@dnd-kit/modifiers'

import { Box, Button, Chip, Stack } from '@mui/material'
import { PlusIcon } from 'shared/icons'
import Grid from '@mui/material/Grid'

import { Loading } from 'components/controls/loading'
import { BlockCaption } from 'components/controls/blockCaption'

import { SearchsDiseaseResult, useListSearchsDisease, useRemoveSearchsDisease, useSortSearchsDisease } from 'interface/search/disease'

import { useSortableList } from '../container/useSortableList'
import { useSortableListEdit } from '../container/useSortableListEdit'
import { EdittableDataListRecord, StaticDataListRecord } from '../components/record'

import { AddSearchsDiseaseDialog } from './dialog/add'
import { EditSearchsDiseaseDialog } from './dialog/edit'
import { ErrorInfo } from 'components/controls/errorInfo'

const SearchsDiseaseCaption = "対象疾患"

const gridRules = {
  size: {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 4,
    xl: 3
  }
}

type DiseaseMark = {
  label: string
  color: 'cancer' | 'dpc'
}
type DiseaseMarkChipProps = {
  mark: DiseaseMark
  first: boolean
  last: boolean
}
const DiseaseMarkChip = ({
  mark,
  first, last
}: DiseaseMarkChipProps) => (
  <Chip
    size="small"
    label={mark.label}
    color={mark.color}
    sx={{
      fontSize: '50%',
      height: 'auto',
      borderTopLeftRadius: first ? '1em' : '0',
      borderBottomLeftRadius: first ? '1em' : '0',
      borderTopRightRadius: last ? '1em' : '0',
      borderBottomRightRadius: last ? '1em' : '0'
    }}
  />
)

type DiseaseMarkChipListProps = {
  disease: SearchsDiseaseResult
}
const DiseaseMarkChipList = ({
  disease
}: DiseaseMarkChipListProps) => {
  const marks = useMemo<DiseaseMark[]>(
    () => flatten([
      disease.cancerCode && disease.cancerYear ? { label: 'がん', color: 'cancer' } : undefined,
      disease.dpcCode && disease.dpcYear ? { label: 'DPC', color: 'dpc' } : undefined,
    ]),
    [disease]
  )
  return <>
    {marks.map((mark, i) =>
      <DiseaseMarkChip
        key={`disease-mark-${i}`}
        mark={mark}
        first={i === 0}
        last={i === (marks.length - 1)}
      />
    )}
  </>
}

type EdittableSearchsDiseaseListBlockProps = {
  searchId: number
}
const EdittableSearchsDiseaseListBlock = ({
  searchId
}: EdittableSearchsDiseaseListBlockProps) => {
  const { loading, data, error } = useListSearchsDisease(searchId)
  const list = useSortableList({ list: data?.list ?? [] })
  const { sortableList } = list
  const [postState, post] = useSortSearchsDisease(searchId)
  const [removeState, remove] = useRemoveSearchsDisease(searchId)
  const {
		modal, closeModal,
		onAdd, onEditIt, onRemoveIt,
		onSubmitEdit, onSubmitAdd,
		onDragEnd
  } = useSortableListEdit({
    ...list,
    post: (data) => post(data?.map(r => r.searchsDiseaseId) ?? [])
  })
  return (
    <Stack direction="column" position="relative">
      <Stack direction="row" spacing={1} alignItems="flex-end" width="100%" >
        <Box flexGrow={1}><BlockCaption flexGrow={1} caption={SearchsDiseaseCaption}/></Box>
        <Button
          startIcon={<PlusIcon />}
          variant="contained"
          size="small"
          sx={{ width: 'auto' }}
          onClick={onAdd}
        >
          追加
        </Button>
      </Stack>
      {error && <ErrorInfo error={error}/>}
      {(loading || postState.posting || removeState.posting) && <Loading/>}

      {sortableList.length > 0 && (
        <Grid container flexGrow={1} spacing={1} marginTop={1}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableList}>
              {sortableList.map((disease, i) => (
                <EdittableDataListRecord
                  key={`disease-${i}`}
                  data={disease}
                  displayName={disease.diseaseName ?? disease.searchWord ?? 'No name'}
                  onEditIt={() => {
                    onEditIt(disease)
                  }}
                  onRemoveIt={() => {
                    remove(disease.searchsDiseaseId)
                    onRemoveIt(disease)
                  }}
                  {...gridRules}
                >
                  <DiseaseMarkChipList disease={disease}/>
                </EdittableDataListRecord>
              ))}
            </SortableContext>
          </DndContext>
        </Grid>
      )}

      { modal?.command === 'add' ? (
        <AddSearchsDiseaseDialog
          searchId={searchId}
          onSubmit={onSubmitAdd}
          onClose={closeModal}
        />
      ) : (modal?.command === 'edit' && modal.data) ? (
        <EditSearchsDiseaseDialog
          searchId={searchId}
          disease={modal.data}
          onSubmit={onSubmitEdit}
          onClose={closeModal}
        />
      ) : (
        <></>
      )}
    </Stack>
  )
}

type StaticSearchsDiseaseListBlockProps = {
  searchId: number
}
const StaticSearchsDiseaseListBlock = ({
  searchId
}: StaticSearchsDiseaseListBlockProps) => {
  const { loading, data, error } = useListSearchsDisease(searchId)
  if (loading || !data || data.list.length === 0) return <></>
  return (
    <Stack direction="column" position="relative">
      <BlockCaption caption={SearchsDiseaseCaption} />
      {error && <ErrorInfo error={error}/>}

      <Grid container flexGrow={1} spacing={1} marginTop={0.5}>
        {data.list.map((disease, i) => (
          <StaticDataListRecord
            key={`disease-${i}`}
            displayName={disease.diseaseName ?? disease.searchWord ?? 'No name'}
            {...gridRules}
          >
            <DiseaseMarkChipList disease={disease}/>
          </StaticDataListRecord>
        ))}
      </Grid>
    </Stack>
  )
}

type SearchsDiseaseListBlockProps = {
  edittable: boolean
  searchId: number
}
export const SearchsDiseaseListBlock = ({
  edittable,
  searchId
}: SearchsDiseaseListBlockProps) => edittable ? (
  <EdittableSearchsDiseaseListBlock
    searchId={searchId}
  />
) : (
  <StaticSearchsDiseaseListBlock
    searchId={searchId}
  />
)
